import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import {
  TextField,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import debounce from "lodash/debounce";
import UseGql from "@/services/graph-ql/useGql";
import { Get_Lead_By_Filters } from "@/modules/crm/leads/graphql-queries/appointment-scheduling";
import UITextField from "@/components/ui-controls/textfield";
import { isGuid } from "@/services/generic.methods";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import GetIcon from "@/components/font-awesome-icon";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";

const analyzeInput = (input) => {
  const payload = {};
  let id = "";
  try {
    id = input?.split("ID:")?.[1]?.trim();
  } catch (error) {
    // console.log(error, "ID not found")
  }
  if (/^\d+$/.test(input) || id) {
    const rawVal = id || input;
    payload.id = { eq: /^\d+$/.test(rawVal) ? +rawVal : rawVal };
  } else if (/@/.test(input)) {
    payload.email = { contains: input };
  } else {
    // payload.firstName = { contains: input };
    // payload.lastName = { contains: input };
    payload.or = [
      { firstName: { contains: input } },
      { lastName: { contains: input } },
      { email: { contains: input } },
    ];
  }

  return payload;
};

export const getPrefillFromSearchInput = (input) => {
  const trimmed = (input || "").trim();
  if (!trimmed) return {};
  return { email: trimmed };
};

const SearchLead = ({
  onSelect,
  selectedLead,
  form,
  isReadOnly,
  lead = {},
  showSearchIcon = true,
  size = "small",
  initialLead = null,
  onCreateNew,
  showCreateNew = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const getLeadByFilters = UseGql(Get_Lead_By_Filters);
  const { client: dataClient, plan } = useCRMDataClient();
  const user = useSelector(getLoggedinUser);

  const setAutoFilledLead = (leads, value) => {
    let id = "";
    try {
      id = `${value}`?.split("ID:");
      id = id[id.length - 1]?.trim();
    } catch (error) {
      // console.log("ID not found")
    }
    if (id) {
      const lead = leads.find((lead) => String(lead.id) === String(id));
      if (lead) {
        onSelect("leadId", lead.id, lead);
      }
    }
  };

  const handleSearch = useCallback(
    debounce(async (value) => {
      if (!value || typeof value == "object") {
        setOptions([]);
        setLoading(false);
        return;
      }
      const payload = analyzeInput(value);
      setLoading(true);

      try {
        const response = await dataClient.leads.getGridItems(
          { where: payload, take: 50, order: [{ id: "DESC" }] },
          {
            query: Get_Lead_By_Filters,
            executeQuery: getLeadByFilters,
            companyKey: user?.CompanyKey,
            isLeadSearch: true,
          }
        );
        const leads = response?.leadsGrid?.items || [];
        setOptions(leads);
        setAutoFilledLead(leads, value);
      } catch (error) {
        console.error("[SearchLead] DataClient search error:", error);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    handleSearch(inputValue);
  }, [inputValue, handleSearch]);

  useEffect(() => {
    if (lead?.id) {
      setOptions([]);
      onSelect("leadId", lead.id, lead);
    }
  }, [lead?.id]);

  useEffect(() => {
    const leadId = form?.values?.leadId;
    if (!leadId || !isReadOnly) return;

    if (lead?.id && String(lead.id) === String(leadId)) {
      setOptions([]);
      onSelect("leadId", lead.id, lead);
      return;
    }

    if (initialLead?.id && String(initialLead.id) === String(leadId)) {
      setOptions([]);
      onSelect("leadId", leadId, initialLead);
      return;
    }

    setOptions([]);
    setLoading(true);
    handleSearch(leadId);
  }, [form?.values?.leadId, isReadOnly, lead?.id, initialLead?.id]);

  const getName = (data) => {
    const firstName =
      data?.firstName ||
      data?.FirstName ||
      data?.contact?.firstName ||
      data?.contact?.FirstName ||
      "";
    const lastName =
      data?.lastName ||
      data?.LastName ||
      data?.contact?.lastName ||
      data?.contact?.LastName ||
      "";
    const name =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName
          ? firstName
          : lastName
            ? lastName
            : "N/A";
    return name;
  };

  const getLeadName = (data) => {
    if (typeof data == "string") return data;
    if (data?.isCreateNew) return "Create New Lead";
    const label = `${getName(data)} (${data?.email || "Email unavailable"})`;
    return isGuid(data?.id) ? label : `${label} ID:${data?.id || "--"}`;
  };

  const Option = (props) => {
    const option = props?.option;
    if (option?.isCreateNew) {
      return (
        <Box
          {...props}
          sx={{
            px: 1,
            py: 1,
            borderTop: "1px solid var(--palette-stroke)",
            cursor: "pointer",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              backgroundColor: "var(--palette-primary_main)",
              color: "var(--palette-primaary_button_text)",
              borderRadius: 1,
              py: 0.75,
              justifyContent: "flex-start",
            }}
          >
            Create New Lead
          </Button>
        </Box>
      );
    }
    return (
      <Box
        {...props}
        sx={{
          color: "var(--palette-text)",
          display: "flex",
          alignItems: "center",
          fontSize: "0.875rem",
          px: 1,
        }}
      >
        {getName(option)} ({option?.email || "Email unavailable"})
        {!isGuid(option?.id) && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "var(--palette-grey_text)",
              pl: 0.5,
            }}
          >
            ID:{option?.id || "--"}
          </Typography>
        )}
      </Box>
    );
  };

  const [open, setOpen] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleBlur = () => {
    setOpen(false);
    setOptions([]);
    setInputValue("");
  };
  const handleFocus = () => {
    setOpen(!!inputValue);
  };
  const handleInputChange = (e) => {
    const { value = "" } = e?.target;
    setInputValue(value);
    setOpen(!!value);
  };
  return (
    <>
      {(form?.values?.leadId || lead.id) && (isReadOnly || initialLead?.id) ? (
        <>
          <UITextField
            size={size}
            title={!isReadOnly ? "Select Lead" : undefined}
            value={
              lead?.id && String(lead.id) === String(form?.values?.leadId)
                ? getLeadName(lead)
                : initialLead?.id &&
                    String(initialLead.id) === String(form?.values?.leadId)
                  ? getLeadName(initialLead)
                  : loading
                    ? "loading.."
                    : (() => {
                        const matched = options.find(
                          (opt) =>
                            String(opt.id) === String(form?.values?.leadId)
                        );
                        if (matched) return getLeadName(matched);
                        return getLeadName(lead);
                      })()
            }
            disabled={isReadOnly}
            postfix={
              loading &&
              !(
                (lead?.id &&
                  String(lead.id) === String(form?.values?.leadId)) ||
                (initialLead?.id &&
                  String(initialLead.id) === String(form?.values?.leadId))
              ) ? (
                <CircularProgress color="inherit" size={20} />
              ) : !isReadOnly && initialLead?.id ? (
                <Box
                  component="span"
                  sx={{ cursor: "pointer", fontSize: "0.875rem", px: 0.5 }}
                  onClick={() => {
                    onSelect("leadId", null, null);
                    setOptions([]);
                  }}
                >
                  <GetIcon icon={faXmark} />
                </Box>
              ) : null
            }
          />
        </>
      ) : (
        <Box>
          <Autocomplete
            open={!inputValue || form?.values?.leadId || lead.id ? false : open}
            openOnFocus
            // freeSolo
            loading={loading}
            loadingText="Searching..."
            size={size}
            options={
              showCreateNew && onCreateNew && options.length > 0 && !loading
                ? [
                    ...options,
                    {
                      id: "create-new",
                      firstName: "",
                      lastName: "Create New Lead",
                      email: "",
                      isCreateNew: true,
                    },
                  ]
                : options
            }
            noOptionsText={
              showCreateNew && onCreateNew ? (
                <Box sx={{ p: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--palette-grey_text)", mb: 1 }}
                  >
                    No leads found.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={() => {
                      onCreateNew(inputValue);
                      setOpen(false);
                      setInputValue("");
                    }}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "var(--palette-primary_main)",
                      color: "var(--palette-primary_button_text)",
                      borderRadius: 1,
                      py: 0.75,
                    }}
                  >
                    Create New Lead &ldquo;{inputValue}&rdquo;
                  </Button>
                </Box>
              ) : (
                "No results found"
              )
            }
            getOptionLabel={(option) => getLeadName(option)}
            slotProps={{ popupIndicator: { sx: { display: "none" } } }}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return <Option key={key} {...rest} option={option} />;
            }}
            sx={{
              "& .MuiAutocomplete-option": {
                backgroundColor: "#000",
              },
              "& .Mui-focused": {
                backgroundColor: "red",
              },
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  size={size}
                  label="Select Lead"
                  variant="outlined"
                  value={inputValue}
                  onChange={(e) => {
                    handleInputChange(e);
                  }}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  error={
                    !!form?.touched?.leadId &&
                    !!form?.errors.leadId &&
                    !form?.values?.leadId
                  }
                  helperText={
                    (form?.touched?.leadId &&
                      !form?.values?.leadId &&
                      form?.errors?.leadId) ||
                    ""
                  }
                  sx={{
                    background: "var(--palette-grid_table)",
                    "& .MuiFormHelperText-root": {
                      position: "absolute",
                      bottom: "-20px",
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      color: "var(--palette-grey_text)",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      paddingRight: form?.values?.leadId
                        ? "40px !important"
                        : "12px !important",
                      color: "var(--palette-text) !important",
                      "& ,MuiAutocomplete-endAdornment": {
                        "& .MuiAutocomplete-clearIndicator svg": {
                          color: "var(--palette-text) !important",
                          fill: "var(--palette-text) !important",
                        },
                      },
                    },
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : showSearchIcon ? (
                          <SearchIcon />
                        ) : (
                          ""
                        )}
                        {params.InputProps.endAdornment &&
                          React.cloneElement(params.InputProps.endAdornment, {
                            children:
                              params.InputProps.endAdornment.props.children.filter(
                                (child) =>
                                  child &&
                                  child?.props?.className !==
                                    "MuiAutocomplete-popupIndicator"
                              ),
                          })}
                      </>
                    ),
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: !!form?.values?.leadId, // disables input text if a value is selected
                  }}
                />
              );
            }}
            onChange={(event, newValue) => {
              if (newValue?.isCreateNew) {
                onCreateNew?.(inputValue);
                setOpen(false);
                setInputValue("");
                return;
              }
              if (newValue) {
                setInputValue("");
                setOptions([]);
                setOpen(false);
                // setInputValue(`${newValue.firstName} (${newValue.email})`);
                if (onSelect) {
                  const matchedLead = options.find(
                    (opt) => String(opt.id) === String(newValue.id)
                  );
                  onSelect("leadId", newValue.id, matchedLead || newValue);
                }
              } else {
                onSelect("leadId", null, null);
              }
            }}
          />
        </Box>
      )}
    </>
  );
};

export default SearchLead;
